import {
  demoConcepts,
  demoLessons,

  demoQuizQuestions,
  demoTopics,
} from "./mock-data";
import type { StudentMasteryState } from "./bkt-engine";
import {
  interpretStudentReasoning,
  buildGapSignalsFromInterpretation,
  createSocraticPrompt,
  shouldTriggerIntervention,
  type InterpretationResult
} from "./interpretation-engine";
import { extractChatReplyText } from "./quiz-generation";
import type {
  AuthSession,
  AuditEvent,
  ApiHealth,
  ChatResponse,
  ChatMessage,
  Concept,
  GapSignal,
  GoogleAuthConfig,
  GoogleStudentRegistration,
  GovernanceSummary,
  InterpretationResult as InterpretationResultType,
  InterventionTrigger,
  Lesson,
  LessonGovernanceMetadata,
  MockEmailMessage,
  NotebookRecord,
  OpenNotebookSource,

  QuizQuestion,
  RecommendedNextStep,
  RegistrationResponse,
  School,
  SignupPaymentConfig,
  StudentProfile,
  StudentChatSession,
  StudentRegistration,
  StudentSignupCheckout,
  StudyMaterial,
  Subject,
  StudentUnderstandingProfile,
  Topic,
  UserAccount,
  UserRole
} from "./types";

declare global {
  interface Window {
    __APP_CONFIG__?: {
      API_BASE_URL?: string;
      DEFAULT_NOTEBOOK_ID?: string;
    };
  }
}

type OpenNotebookBundle = {
  notebooks: NotebookRecord[];
  defaultNotebookId: string;
  subjects: Subject[];
  topics: Topic[];
  lessons: Lesson[];
  materials: StudyMaterial[];
  governance: Record<string, LessonGovernanceMetadata>;
  accounts: UserAccount[];
  schools: School[];
  auditEvents: AuditEvent[];
};

type ChatSessionResponse = {
  id?: string;
  session_id?: string;
};

type NoteRecord = {
  id: string;
  title?: string | null;
  content?: string | null;
  note_type?: string | null;
  created?: string;
  updated?: string;
};

type OpenNotebookChatResponse = {
  answer?: string;
  response?: string;
  content?: string;
  message?: string;
};

type NotebookChatWarmEntry = {
  notebookId: string;
  sessionId: string | null;
};

export type AssessmentReadyNotification = {
  type: "assessment-ready";
  studentId: string;
  subjectId: string;
  quizId?: string;
  questionCount: number;
  topicName?: string;
  createdAt: string;
};

const DEFAULT_HEADERS: HeadersInit = {
  "Content-Type": "application/json"
};

const AUTH_TOKEN_STORAGE_KEY = "classroom-notebook-auth-token";
const WRAPPER_STORAGE_KEY = "classroom-notebook-wrapper-state-v1";
const notebookChatWarmCache: Record<string, NotebookChatWarmEntry> = {};

type WrapperState = {
  accounts: UserAccount[];
  schools: School[];
  subjects: Subject[];
  topics: Topic[];
  topicOverrides: Record<string, Partial<Topic>>;
  materials: StudyMaterial[];
  governanceByLessonId: Record<string, LessonGovernanceMetadata>;
  auditEvents: AuditEvent[];
};

export class WrapperApiError extends Error {
  status: number;
  paymentRequired: boolean;
  checkoutUrl?: string;
  reference?: string;

  constructor(message: string, status: number, detail?: Record<string, unknown>) {
    super(message);
    this.name = "WrapperApiError";
    this.status = status;
    this.paymentRequired = detail?.paymentRequired === true;
    this.checkoutUrl = typeof detail?.checkoutUrl === "string" ? detail.checkoutUrl : undefined;
    this.reference = typeof detail?.reference === "string" ? detail.reference : undefined;
  }
}

export function isPaymentRequiredError(error: unknown): error is WrapperApiError {
  return error instanceof WrapperApiError && error.paymentRequired && Boolean(error.checkoutUrl);
}

type GovernanceNoteDocument = {
  kind: "classroom-governance";
  lessonId: string;
  governance: LessonGovernanceMetadata;
};

type RolesNoteDocument = {
  kind: "classroom-roles";
  accounts: UserAccount[];
};

type SchoolsNoteDocument = {
  kind: "classroom-schools";
  schools: School[];
};

type AuditNoteDocument = {
  kind: "classroom-audit";
  events: AuditEvent[];
};

function getConfig() {
  const runtimeConfig =
    typeof window !== "undefined" ? window.__APP_CONFIG__ || {} : {};

  return {
    apiBaseUrl:
      runtimeConfig.API_BASE_URL?.replace(/\/$/, "") ||
      import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "") ||
      "http://localhost:4000",
    defaultNotebookId:
      runtimeConfig.DEFAULT_NOTEBOOK_ID ||
      import.meta.env.VITE_DEFAULT_NOTEBOOK_ID ||
      ""
  };
}

function getProxyUrl() {
  const { apiBaseUrl } = getConfig();
  if (!apiBaseUrl) {
    throw new Error("Backend API URL is required.");
  }
  return `${apiBaseUrl}/api/open-notebook`;
}

function hasWrapperApi() {
  return Boolean(getConfig().apiBaseUrl);
}

function requireWrapperApi() {
  if (!hasWrapperApi()) {
    throw new Error("Backend API URL is required. Classroom Notebook cannot run without the backend API.");
  }
}

function wait<T>(data: T): Promise<T> {
  return Promise.resolve(data);
}

function canUseStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function readWrapperState(): WrapperState {
  const fallback: WrapperState = {
    accounts: [],
    schools: [],
    subjects: [],
    topics: [],
    topicOverrides: {},
    materials: [],
    governanceByLessonId: {},
    auditEvents: []
  };

  if (!canUseStorage()) {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(WRAPPER_STORAGE_KEY);
    return raw ? ({ ...fallback, ...(JSON.parse(raw) as WrapperState) }) : fallback;
  } catch {
    return fallback;
  }
}

function writeWrapperState(state: WrapperState) {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.setItem(WRAPPER_STORAGE_KEY, JSON.stringify(state));
}

export function clearWrapperState() {
  if (!canUseStorage()) {
    return;
  }
  window.localStorage.removeItem(WRAPPER_STORAGE_KEY);
  window.location.reload();
}

export function getAuthToken() {
  if (!canUseStorage()) {
    return "";
  }

  return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || "";
}

export function openNotificationSocket(
  onNotification: (notification: AssessmentReadyNotification) => void,
  onError?: (error: Event) => void
) {
  const token = getAuthToken();
  const { apiBaseUrl } = getConfig();
  if (!token || !apiBaseUrl) {
    throw new Error("Authenticated backend API is required for notifications.");
  }

  const wsBaseUrl = apiBaseUrl.replace(/^http:/, "ws:").replace(/^https:/, "wss:").replace(/\/$/, "");
  const socket = new WebSocket(`${wsBaseUrl}/notifications?token=${encodeURIComponent(token)}`);

  socket.addEventListener("message", (event) => {
    try {
      const payload = JSON.parse(event.data);
      if (payload?.type === "assessment-ready") {
        onNotification(payload as AssessmentReadyNotification);
      }
    } catch {
      // Ignore malformed notification payloads.
    }
  });

  if (onError) {
    socket.addEventListener("error", onError);
  }

  return socket;
}

function setAuthToken(token: string) {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
}

export function clearAuthToken() {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
}

async function fetchOpenNotebookNotes(notebookId: string): Promise<NoteRecord[]> {
  return proxyOpenNotebook<NoteRecord[]>(`/notebooks/${encodeURIComponent(notebookId)}/notes`);
}

async function createOpenNotebookNote(
  notebookId: string,
  title: string,
  content: string
): Promise<NoteRecord> {
  return proxyOpenNotebook<NoteRecord>("/notes", {
    method: "POST",
    body: JSON.stringify({
      notebook_id: notebookId,
      title,
      content,
      note_type: "human"
    })
  });
}

async function updateOpenNotebookNote(
  noteId: string,
  title: string,
  content: string
): Promise<NoteRecord> {
  return proxyOpenNotebook<NoteRecord>(`/notes/${noteId}`, {
    method: "PUT",
    body: JSON.stringify({
      title,
      content,
      note_type: "human"
    })
  });
}

async function upsertOpenNotebookNote(
  notebookId: string,
  title: string,
  content: string
): Promise<NoteRecord> {
  const notes = await fetchOpenNotebookNotes(notebookId);
  const existing = notes.find((note) => note.title === title);
  return existing
    ? updateOpenNotebookNote(existing.id, title, content)
    : createOpenNotebookNote(notebookId, title, content);
}

async function postOpenNotebookChatExecute(
  sessionId: string,
  message: string,
  context: Record<string, unknown>,
  timeoutMs = 25000
): Promise<string> {
  const proxyUrl = getProxyUrl();

  const controller = typeof AbortController !== "undefined" ? new AbortController() : undefined;
  const timeout = setTimeout(() => controller?.abort(), timeoutMs);

  try {
    const response = await fetchWithRetry(`${proxyUrl}/chat/execute`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ session_id: sessionId, message, context }),
      signal: controller?.signal
    });

    const contentType = response.headers.get("content-type") || "";
    const raw = await response.text();

    if (!response.ok) {
      throw new Error(raw || `Chat execute failed (${response.status})`);
    }

    if (contentType.includes("application/json")) {
      return extractChatReplyText(JSON.parse(raw));
    }

    let aggregated = "";
    for (const line of raw.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) continue;
      const payload = trimmed.slice(5).trim();
      if (!payload || payload === "[DONE]") continue;
      try {
        const parsed = JSON.parse(payload) as Record<string, unknown>;
        if (parsed.done) continue;
        aggregated +=
          String(parsed.delta ?? parsed.answer ?? parsed.content ?? parsed.message ?? "");
      } catch {
        aggregated += payload;
      }
    }

    return aggregated || raw;
  } finally {
    clearTimeout(timeout);
  }
}

async function proxyOpenNotebook<T>(path: string, init?: RequestInit): Promise<T> {
  const proxyUrl = getProxyUrl();

  const url = `${proxyUrl}${path}`;
  console.log('[API] proxyOpenNotebook URL:', url);

  const response = await fetchWithRetry(url, {
    ...init,
    headers: {
      ...DEFAULT_HEADERS,
      ...(init?.headers ?? {})
    }
  });

  console.log('[API] proxyOpenNotebook status:', response.status, response.statusText);

  if (!response.ok) {
    const detail = await response.text();
    console.log('[API] proxyOpenNotebook error detail:', detail.substring(0, 200));
    throw new Error(detail || `Open Notebook proxy failed with ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchWithRetry(input: RequestInfo | URL, init?: RequestInit, retries = 2): Promise<Response> {
  let attempt = 0;
  let lastError: unknown;

  while (attempt <= retries) {
    try {
      const response = await fetch(input, init);
      if (response.status >= 500 && attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
        attempt += 1;
        continue;
      }
      return response;
    } catch (error) {
      lastError = error;
      if (attempt >= retries) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
      attempt += 1;
    }
  }

  throw lastError instanceof Error ? lastError : new Error("Open Notebook request failed after retries.");
}

export async function requestUnauthenticatedApi<T>(path: string, init?: RequestInit): Promise<T> {
  const { apiBaseUrl } = getConfig();

  if (!apiBaseUrl) {
    throw new Error("Wrapper API URL is not configured.");
  }

  const headers: Record<string, string> = {
    ...(init?.method !== "DELETE" && init?.method !== "GET" ? { "Content-Type": "application/json" } : {}),
    ...(init?.headers as Record<string, string> ?? {})
  };

  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers
  });

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(detail || `Wrapper API request failed with ${response.status}`);
  }

  return (await response.json()) as T;
}

export async function requestWrapperApi<T>(path: string, init?: RequestInit): Promise<T> {
  const { apiBaseUrl } = getConfig();

  if (!apiBaseUrl) {
    throw new Error("Wrapper API URL is not configured.");
  }

  const authToken = getAuthToken();
  const isDeleteOrGet = init?.method === "DELETE" || init?.method === "GET";
  
  const headers: Record<string, string> = {};
  if (!isDeleteOrGet) {
    headers["Content-Type"] = "application/json";
  }
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: {
      ...headers,
      ...(init?.headers as Record<string, string> ?? {})
    }
  });

  if (!response.ok) {
    const detail = await response.text();
    let errorMessage: string;
    let parsed: Record<string, unknown> | undefined;
    try {
      parsed = JSON.parse(detail);
      errorMessage = typeof parsed.message === "string" ? parsed.message : detail;
    } catch {
      errorMessage = detail || `Wrapper API request failed with ${response.status}`;
    }
    throw new WrapperApiError(errorMessage, response.status, parsed);
  }

  return (await response.json()) as T;
}

export async function login(email: string, password: string): Promise<UserAccount> {
  const session = await requestWrapperApi<AuthSession>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password })
  });
  setAuthToken(session.token);
  return session.user;
}

export async function fetchGoogleAuthConfig(): Promise<GoogleAuthConfig> {
  requireWrapperApi();
  return requestWrapperApi<GoogleAuthConfig>("/auth/google-config", {
    method: "GET"
  });
}

export async function fetchSignupPaymentConfig(): Promise<SignupPaymentConfig> {
  requireWrapperApi();
  return requestWrapperApi<SignupPaymentConfig>("/auth/signup-payment-config", {
    method: "GET"
  });
}

export async function loginWithGoogle(credential: string): Promise<UserAccount> {
  const session = await requestWrapperApi<AuthSession>("/auth/login/google", {
    method: "POST",
    body: JSON.stringify({ credential })
  });
  setAuthToken(session.token);
  return session.user;
}

export async function registerStudent(registration: StudentRegistration): Promise<StudentSignupCheckout> {
  requireWrapperApi();
  return requestWrapperApi<StudentSignupCheckout>("/auth/register/student", {
    method: "POST",
    body: JSON.stringify({
      fullName: registration.fullName,
      email: registration.email,
      password: registration.password,
      schoolName: registration.schoolName
    })
  });
}

export async function registerStudentWithGoogle(registration: GoogleStudentRegistration): Promise<StudentSignupCheckout> {
  requireWrapperApi();
  return requestWrapperApi<StudentSignupCheckout>("/auth/register/student/google", {
    method: "POST",
    body: JSON.stringify({
      credential: registration.credential,
      schoolName: registration.schoolName
    })
  });
}

export async function confirmStudentSignupPayment(reference: string): Promise<RegistrationResponse> {
  requireWrapperApi();
  const response = await requestWrapperApi<RegistrationResponse>("/auth/register/student/confirm-payment", {
    method: "POST",
    body: JSON.stringify({ reference })
  });
  if (response.token) {
    setAuthToken(response.token);
  }
  return response;
}

export async function fetchAvailableSchools(): Promise<School[]> {
  requireWrapperApi();
  return requestWrapperApi<School[]>("/schools");
}

export async function fetchCurrentUser(): Promise<UserAccount | null> {
  if (!hasWrapperApi() || !getAuthToken()) {
    return null;
  }

  try {
    return await requestWrapperApi<UserAccount>("/auth/me");
  } catch {
    clearAuthToken();
    return null;
  }
}

export async function logout() {
  if (hasWrapperApi() && getAuthToken()) {
    try {
      await requestWrapperApi<{ ok: boolean }>("/auth/logout", {
        method: "POST"
      });
    } catch {
      // ignore logout transport failures and clear local session anyway
    }
  }

  clearAuthToken();
}

export async function updateMyProfile(input: {
  fullName: string;
  organizationName: string;
}) {
  return requestWrapperApi<UserAccount>("/auth/me/profile", {
    method: "PUT",
    body: JSON.stringify(input)
  });
}

export async function updateMyEmail(email: string) {
  return requestWrapperApi<UserAccount>("/auth/me/email", {
    method: "PUT",
    body: JSON.stringify({ email })
  });
}

export async function requestMyPasswordReset() {
  return requestWrapperApi<{ ok: boolean }>("/auth/me/reset-password", {
    method: "POST"
  });
}

export async function confirmPasswordReset(token: string, newPassword: string) {
  clearAuthToken();
  return requestWrapperApi<{ ok: boolean }>("/auth/reset-password/confirm", {
    method: "POST",
    body: JSON.stringify({ token, newPassword })
  });
}

export type LegalDocument = {
  doc_type: "privacy" | "terms" | "minors-consent";
  version: string;
  title: string;
  content: string;
};

export async function fetchLegalDocuments() {
  return requestWrapperApi<LegalDocument[]>("/auth/legal-documents");
}

export async function fetchMyConsent() {
  return requestWrapperApi<{
    privacy_version?: string;
    terms_version?: string;
    minors_consent_version?: string;
    accepted_at?: string;
  } | null>("/auth/me/consent");
}

export async function acceptMyConsent(input: {
  privacyVersion: string;
  termsVersion: string;
  minorsConsentVersion?: string;
}) {
  return requestWrapperApi<{ ok: boolean }>("/auth/me/consent", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function requestMyDataDeletion() {
  return requestWrapperApi<{ ok: boolean }>("/auth/me/delete-data", {
    method: "POST"
  });
}

export async function fetchMockEmailOutbox() {
  return [];
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function extensionFromFileName(fileName: string) {
  const parts = fileName.toLowerCase().split(".");
  return parts.length > 1 ? `.${parts.pop()}` : "";
}

function classifyMaterialFile(fileName: string, mimeType: string) {
  const extension = extensionFromFileName(fileName);
  const normalizedMime = mimeType.toLowerCase();

  if (normalizedMime === "application/pdf" || extension === ".pdf") {
    return { fileCategory: "pdf", materialType: "pdf" } as const;
  }

  if (
    normalizedMime.includes("presentation") ||
    normalizedMime === "application/vnd.ms-powerpoint" ||
    extension === ".ppt" ||
    extension === ".pptx"
  ) {
    return { fileCategory: "slides", materialType: "slides" } as const;
  }

  if (
    normalizedMime.startsWith("image/") ||
    [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"].includes(extension)
  ) {
    return { fileCategory: "image", materialType: "image" } as const;
  }

  if (
    [
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "application/rtf"
    ].includes(normalizedMime) ||
    [".doc", ".docx", ".txt", ".rtf"].includes(extension)
  ) {
    return { fileCategory: "document", materialType: "document" } as const;
  }

  throw new Error("Unsupported file type. Upload a PDF, slides, document, or image.");
}

async function fileToBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the selected file."));
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const [, base64 = ""] = result.split(",", 2);
      resolve(base64);
    };
    reader.readAsDataURL(file);
  });
}

function shortCodeFromName(value: string) {
  const parts = value
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) {
    return "";
  }

  const initials = parts.map((part) => part[0]?.toUpperCase() || "").join("");
  return (initials || value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6)).toUpperCase().slice(0, 12);
}

function firstString(value: unknown, fallback: string) {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function withApiAssetUrl(material: StudyMaterial): StudyMaterial {
  if (!material.asset?.assetUrl) {
    return material;
  }

  if (/^https?:\/\//.test(material.asset.assetUrl)) {
    return material;
  }

  return {
    ...material,
    asset: {
      ...material.asset,
      assetUrl: `${getConfig().apiBaseUrl}${material.asset.assetUrl}`
    }
  };
}

function textFromSource(source: OpenNotebookSource) {
  return (
    source.transcription ||
    source.text ||
    (typeof source.metadata?.transcript === "string"
      ? source.metadata.transcript
      : "") ||
    ""
  );
}

function stringArrayFromMetadata(source: OpenNotebookSource, key: string): string[] {
  const value = source.metadata?.[key];
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function getSubjectName(source: OpenNotebookSource) {
  return firstString(source.metadata?.subject, "General");
}

function getTopicName(source: OpenNotebookSource) {
  return firstString(source.metadata?.topic, source.title || "Lesson topic");
}

function getUnderstandingGoal(source: OpenNotebookSource) {
  return firstString(
    source.metadata?.understanding_goal,
    "Students should understand the concepts required to make sense of this lesson."
  );
}

function governanceFromSource(source: OpenNotebookSource): LessonGovernanceMetadata {
  const topicId = `topic-${slugify(getTopicName(source))}`;
  const prerequisiteTopicIds = stringArrayFromMetadata(source, "prerequisite_topics").map(
    (value) => `topic-${slugify(value)}`
  );
  const lessonDependencyIds = stringArrayFromMetadata(source, "lesson_dependencies");
  const materials = materialsFromSource(
    source,
    `subject-${slugify(getSubjectName(source))}`
  );

  return {
    lessonId: source.id,
    topicId,
    understandingGoal: getUnderstandingGoal(source),
    prerequisiteTopicIds,
    lessonDependencyIds,
    publicationStatus:
      source.metadata?.publication_status === "published" ? "published" : "draft",
    publishingNotes: firstString(source.metadata?.publishing_notes, ""),
    approvedMaterialIds: materials
      .filter((material) => material.sourceAuthority === "approved")
      .map((material) => material.id),
    managedMaterials: materials
  };
}

function governanceNoteTitle(lessonId: string) {
  return `classroom-governance:${lessonId}`;
}

function rolesNoteTitle() {
  return "classroom-roles";
}

function auditNoteTitle() {
  return "classroom-audit";
}

function schoolsNoteTitle() {
  return "classroom-schools";
}

function parseJsonNote<T>(note: NoteRecord): T | null {
  if (!note.content) {
    return null;
  }

  try {
    return JSON.parse(note.content) as T;
  } catch {
    return null;
  }
}

function governanceFromNotes(notes: NoteRecord[]) {
  const records: Record<string, LessonGovernanceMetadata> = {};

  for (const note of notes) {
    if (!note.title?.startsWith("classroom-governance:")) {
      continue;
    }

    const parsed = parseJsonNote<GovernanceNoteDocument>(note);
    if (parsed?.kind === "classroom-governance") {
      records[parsed.lessonId] = parsed.governance;
    }
  }

  return records;
}

function accountsFromNotes(notes: NoteRecord[]) {
  const note = notes.find((item) => item.title === rolesNoteTitle());
  const parsed = note ? parseJsonNote<RolesNoteDocument>(note) : null;
  return parsed?.kind === "classroom-roles" ? parsed.accounts : [];
}

function auditFromNotes(notes: NoteRecord[]) {
  const note = notes.find((item) => item.title === auditNoteTitle());
  const parsed = note ? parseJsonNote<AuditNoteDocument>(note) : null;
  return parsed?.kind === "classroom-audit" ? parsed.events : [];
}

function schoolsFromNotes(notes: NoteRecord[]) {
  const note = notes.find((item) => item.title === schoolsNoteTitle());
  const parsed = note ? parseJsonNote<SchoolsNoteDocument>(note) : null;
  return parsed?.kind === "classroom-schools" ? parsed.schools : [];
}

function summaryFromSource(source: OpenNotebookSource) {
  const rawSummary =
    source.summary ||
    (typeof source.metadata?.summary === "string"
      ? source.metadata.summary
      : "");
  const transcript = textFromSource(source);
  const seed =
    rawSummary ||
    transcript ||
    "No lesson summary is available yet. The wrapper should request an API-generated summary.";

  const focusAreas = seed
    .split(/[\n,.]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 4);

  return {
    highLevelSummary: seed.slice(0, 260),
    focusAreas: focusAreas.length ? focusAreas : ["Summary pending"],
    conceptBreakdown: (focusAreas.length ? focusAreas : ["Summary pending"]).map(
      (value, index) => ({
        title: `Concept ${index + 1}`,
        description: value
      })
    )
  };
}

function materialsFromSource(source: OpenNotebookSource, subjectId: string): StudyMaterial[] {
  const raw = source.metadata?.source_materials;
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.flatMap((item, index) => {
    if (!item || typeof item !== "object") {
      return [];
    }

    const record = item as Record<string, unknown>;
    if (typeof record.title !== "string" || typeof record.excerpt !== "string") {
      return [];
    }

    const topicIds = stringArrayFromMetadata(source, "prerequisite_topics");
    const topicId = `topic-${slugify(getTopicName(source))}`;

    return [
      {
        id: typeof record.id === "string" ? record.id : `${source.id}-material-${index}`,
        subjectId,
        topicIds: [topicId, ...topicIds.map((value) => `topic-${slugify(value)}`)],
        title: record.title,
        materialType:
          record.type === "textbook" ||
          record.type === "teacher-note" ||
          record.type === "worksheet" ||
          record.type === "recording"
            ? record.type
            : "teacher-note",
        sourceAuthority: record.status === "draft" ? "draft" : "approved",
        excerpt: record.excerpt,
        citation: typeof record.citation === "string" ? record.citation : undefined
      }
    ];
  });
}

function lessonFromSource(source: OpenNotebookSource): Lesson {
  const subjectName = getSubjectName(source);
  const topicName = getTopicName(source);
  const subjectId = `subject-${slugify(subjectName)}`;
  const topicId = `topic-${slugify(topicName)}`;
  const materials = materialsFromSource(source, subjectId);
  const governance = governanceFromSource(source);

  return {
    id: source.id,
    notebookId: source.notebook_id,
    sourceId: source.id,
    subjectId,
    topicId,
    lessonDependencyIds: governance.lessonDependencyIds,
    title: source.title || "Untitled lesson",
    teacherName: firstString(source.metadata?.teacher, "Teacher"),
    recordedAt: source.created_at || new Date().toISOString(),
    durationLabel: firstString(source.metadata?.duration, "Unknown length"),
    audioUrl: typeof source.metadata?.audio_url === "string" ? source.metadata.audio_url : "",
    transcript: textFromSource(source),
    summary: summaryFromSource(source),
    sourceMaterialIds: materials.map((material) => material.id),
    publicationStatus: governance.publicationStatus,
    publishingNotes: governance.publishingNotes
  };
}

function topicFromSource(source: OpenNotebookSource): Topic {
  const subjectId = `subject-${slugify(getSubjectName(source))}`;
  const topicName = getTopicName(source);

  return {
    id: `topic-${slugify(topicName)}`,
    subjectId,
    name: topicName,
    description: firstString(
      source.metadata?.topic_description,
      `Understanding support for ${topicName}.`
    ),
    prerequisiteTopicIds: governanceFromSource(source).prerequisiteTopicIds,
    understandingGoal: governanceFromSource(source).understandingGoal
  };
}

function subjectFromSource(source: OpenNotebookSource): Subject {
  const subjectName = getSubjectName(source);

  return {
    id: `subject-${slugify(subjectName)}`,
    name: subjectName,
    description: firstString(
      source.metadata?.subject_description,
      `Subject material synchronized from Open Notebook for ${subjectName}.`
    )
  };
}

function quizFromLesson(
  lesson: Lesson,
  topicLookup: Map<string, Topic>,
  materials: StudyMaterial[]
): QuizQuestion[] {
  const topic = topicLookup.get(lesson.topicId);
  const prereqIds = topic?.prerequisiteTopicIds || [];
  const approvedMaterials = materials.filter(
    (material) =>
      lesson.sourceMaterialIds.includes(material.id) && material.sourceAuthority === "approved"
  );

  if (!prereqIds.length) {
    return [
      {
        id: `${lesson.id}-quiz-1`,
        lessonId: lesson.id,
        prompt: `Which approved study material best supports understanding ${lesson.title}?`,
        choices: [
          approvedMaterials[0]?.title || lesson.summary.focusAreas[0] || "Approved material",
          "It is unrelated to the lesson",
          "It only applies to teacher workflow",
          "It replaces all prerequisite understanding"
        ],
        correctAnswer: approvedMaterials[0]?.title || lesson.summary.focusAreas[0] || "Approved material",
        explanation:
          "This governed question is built from approved lesson materials when no prerequisite graph exists.",
        conceptTag: topic?.name || lesson.title,
        prerequisiteTopicIds: [],
        sourceCitation: approvedMaterials[0]?.citation
      }
    ];
  }

  return prereqIds.map((prerequisiteTopicId, index) => {
    const prerequisiteTopic = topicLookup.get(prerequisiteTopicId);
    const supportingMaterial =
      approvedMaterials.find((material) => material.topicIds.includes(prerequisiteTopicId)) ||
      approvedMaterials[index] ||
      approvedMaterials[0];

    return {
      id: `${lesson.id}-quiz-${index + 1}`,
      lessonId: lesson.id,
      prompt: `Which prior concept from the approved study pack must be understood to properly understand ${topic?.name || lesson.title}?`,
      choices: [
        prerequisiteTopic?.name || "Prerequisite topic",
        "Only the recording duration",
        "Only the teacher name",
        "No prior understanding is required"
      ],
      correctAnswer: prerequisiteTopic?.name || "Prerequisite topic",
      explanation:
        "This governed question is generated from the prerequisite graph and approved study materials attached to the lesson.",
      conceptTag: prerequisiteTopic?.name || "Prerequisite understanding",
      prerequisiteTopicIds: [prerequisiteTopicId],
      sourceCitation: supportingMaterial?.citation || supportingMaterial?.title
    };
  });
}

async function fetchOpenNotebookNotebooks(): Promise<NotebookRecord[]> {
  const records = await proxyOpenNotebook<NotebookRecord[] | { items?: NotebookRecord[] }>(
    "/notebooks"
  );

  return Array.isArray(records) ? records : asArray<NotebookRecord>(records.items);
}

async function fetchOpenNotebookSources(notebookId: string): Promise<OpenNotebookSource[]> {
  const response = await proxyOpenNotebook<OpenNotebookSource[] | { items?: OpenNotebookSource[] }>(
    `/notebooks/${encodeURIComponent(notebookId)}/sources`
  );

  return Array.isArray(response) ? response : asArray<OpenNotebookSource>(response.items);
}

export async function fetchStudentNotebook(studentId: string, subjectId: string): Promise<string | null> {
  const { apiBaseUrl } = getConfig();
  if (!apiBaseUrl) return null;

  try {
    const res = await requestWrapperApi<{ notebookId: string | null }>(
      `/students/${encodeURIComponent(studentId)}/subjects/${encodeURIComponent(subjectId)}/notebook`
    );
    return res.notebookId;
  } catch {
    return null;
  }
}

export async function uploadMaterialToOpenNotebook(
  material: StudyMaterial,
  file?: File,
  preferredNotebookId?: string
): Promise<OpenNotebookSource> {
  const { defaultNotebookId } = getConfig();
  const proxyUrl = getProxyUrl();

  let notebookId = preferredNotebookId || defaultNotebookId;
  if (!notebookId) {
    const notebooks = await fetchOpenNotebookNotebooks();
    notebookId = notebooks[0]?.id;
  }

  if (!notebookId) {
    throw new Error("No Open Notebook notebook is available for upload.");
  }

  const formData = new FormData();
  formData.append("notebook_id", notebookId);
  formData.append("title", material.title);
  formData.append("type", material.materialType || "upload");

  if (file) {
    formData.append("file", file);
  } else if (material.excerpt) {
    formData.append("content", material.excerpt);
  }

  const metadata = {
    materialId: material.id,
    subjectId: material.subjectId,
    topicIds: material.topicIds,
    sourceAuthority: material.sourceAuthority,
    citation: material.citation,
    schoolId: material.schoolId
  };
  formData.append("metadata", JSON.stringify(metadata));
  formData.append("embed", "true");

  const response = await fetch(`${proxyUrl}/sources`, {
    method: "POST",
    body: formData
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to upload to Open Notebook: ${response.status} - ${errorText}`);
  }

  const source = await response.json();
  console.log("Material uploaded to Open Notebook:", source);
  return source as OpenNotebookSource;
}

function emptyOpenNotebookBundle(): OpenNotebookBundle {
  return {
    notebooks: [],
    defaultNotebookId: "",
    subjects: [],
    topics: [],
    lessons: [],
    materials: [],
    governance: {},
    accounts: [],
    schools: [],
    auditEvents: []
  };
}

async function fetchOpenNotebookBundle(
  preferredNotebookId?: string,
  allowEmptyNotebook = false
): Promise<OpenNotebookBundle> {
  const { defaultNotebookId } = getConfig();

  const notebooks = await fetchOpenNotebookNotebooks();
  const notebookId = preferredNotebookId || defaultNotebookId || notebooks[0]?.id || "";

  if (!notebookId) {
    if (allowEmptyNotebook) {
      return emptyOpenNotebookBundle();
    }
    throw new Error("Open Notebook is configured but no notebook is available.");
  }

  const [sources, notes] = await Promise.all([
    fetchOpenNotebookSources(notebookId),
    fetchOpenNotebookNotes(notebookId)
  ]);
  const lessons = sources.map(lessonFromSource);
  const subjects = dedupeById(sources.map(subjectFromSource));
  const topics = dedupeById(sources.map(topicFromSource));
  const materials = dedupeById(
    sources.flatMap((source) =>
      materialsFromSource(source, `subject-${slugify(getSubjectName(source))}`)
    )
  );
  const governance = Object.fromEntries(
    sources.map((source) => [source.id, governanceFromSource(source)])
  );
  const noteGovernance = governanceFromNotes(notes);
  const noteAccounts = accountsFromNotes(notes);
  const noteSchools = schoolsFromNotes(notes);
  const noteAudit = auditFromNotes(notes);

  return {
    notebooks,
    defaultNotebookId: notebookId,
    subjects,
    topics,
    lessons,
    materials,
    governance: { ...governance, ...noteGovernance },
    accounts: noteAccounts,
    schools: noteSchools,
    auditEvents: noteAudit
  };
}

export type AppBootstrap = {
  accounts: UserAccount[];
  schools: School[];
  notebooks: NotebookRecord[];
  defaultNotebookId: string;
  subjects: Subject[];
  topics: Topic[];
  lessons: Lesson[];
  apiHealth: ApiHealth[];
  materials: StudyMaterial[];
  auditEvents: AuditEvent[];
};

export async function fetchBootstrap(preferredNotebookId?: string, skipUserAccounts?: boolean): Promise<AppBootstrap> {
  const hasWrapper = hasWrapperApi();

  if (hasWrapper && !getAuthToken()) {
    throw new Error("Authentication required.");
  }

  const openNotebookBundle = await fetchOpenNotebookBundle(preferredNotebookId, skipUserAccounts);
  const wrapperState = readWrapperState();

  let backendAccounts: UserAccount[] = [];
  let backendSchools: School[] = [];
  let backendSubjects: Subject[] = [];
  let backendTopics: Topic[] = [];
  let backendMaterials: StudyMaterial[] = [];
  let backendAudit: AuditEvent[] = [];

  const wrapperApiBase = getConfig().apiBaseUrl;
  if (hasWrapper && getAuthToken()) {
    try {
      let accountsRes: UserAccount[] = [];
      if (!skipUserAccounts) {
        accountsRes = await requestWrapperApi<UserAccount[]>("/users").catch(() => []);
      }
      const [schoolsRes, subjectsRes, topicsRes, materialsRes] = await Promise.all([
        requestWrapperApi<School[]>("/schools").catch(() => []),
        requestWrapperApi<Subject[]>("/subjects").catch(() => []),
        requestWrapperApi<Topic[]>("/topics").catch(() => []),
        requestWrapperApi<StudyMaterial[]>("/study-materials").catch(() => [])
      ]);
      backendAccounts = accountsRes;
      backendSchools = schoolsRes;
      backendSubjects = subjectsRes;
      backendTopics = topicsRes;
      backendMaterials = materialsRes.map(withApiAssetUrl);
    } catch (err) {
      console.warn("Failed to fetch from wrapper API:", err);
    }
  }

  return buildBootstrapWithWrapperState(
    {
      accounts: backendAccounts.length > 0 ? backendAccounts : openNotebookBundle.accounts,
      schools: backendSchools,
      notebooks: openNotebookBundle.notebooks,
      defaultNotebookId: openNotebookBundle.defaultNotebookId,
      subjects: backendSubjects.length > 0 ? backendSubjects : openNotebookBundle.subjects,
      topics: backendTopics,
      lessons: openNotebookBundle.lessons,
      apiHealth: [
        { domain: "Wrapper API", status: hasWrapper ? "ready" : "missing", notes: hasWrapper ? "Connected to backend" : "Backend not configured" },
        { domain: "Open Notebook", status: "ready", notes: "Connected" }
      ],
      materials: [...backendMaterials, ...openNotebookBundle.materials],
      auditEvents: backendAudit
    },
    wrapperState
  );
}

function buildBootstrapWithWrapperState(
  base: AppBootstrap,
  wrapperState: WrapperState
) {
  const governanceByLessonId = { ...wrapperState.governanceByLessonId };
  const sourceTopics = base.topics.length > 0 ? base.topics : wrapperState.topics;
  const mergedTopics = sourceTopics.map((topic) => {
    const override = wrapperState.topicOverrides[topic.id] || {};
    const lessonGovernance = Object.values(governanceByLessonId).find(
      (item) => item.topicId === topic.id
    );

    return {
      ...topic,
      prerequisiteTopicIds:
        lessonGovernance?.prerequisiteTopicIds || override.prerequisiteTopicIds || topic.prerequisiteTopicIds,
      understandingGoal:
        lessonGovernance?.understandingGoal || override.understandingGoal || topic.understandingGoal,
      ...override
    };
  });
  const topicLookup = new Map(mergedTopics.map((topic) => [topic.id, topic]));
  const materialsById = new Map<string, StudyMaterial>();
  const governanceMaterials = Object.values(governanceByLessonId).flatMap(
    (item) => item.managedMaterials || []
  );

  for (const material of [...base.materials, ...governanceMaterials, ...wrapperState.materials]) {
    materialsById.set(material.id, material);
  }

  const mergedLessons = base.lessons.map((lesson) => {
    const governance = governanceByLessonId[lesson.id];
    const topic = topicLookup.get(governance?.topicId || lesson.topicId);
    return {
      ...lesson,
      topicId: governance?.topicId || lesson.topicId,
      subjectId: topic?.subjectId || lesson.subjectId,
      lessonDependencyIds: governance?.lessonDependencyIds || lesson.lessonDependencyIds || [],
      publicationStatus: governance?.publicationStatus || lesson.publicationStatus || "draft",
      publishingNotes: governance?.publishingNotes || lesson.publishingNotes || "",
      sourceMaterialIds: dedupeStrings([
        ...lesson.sourceMaterialIds,
        ...Object.values(materialsById)
          .filter((material) => material.topicIds.includes(topic?.id || lesson.topicId))
          .map((material) => material.id)
      ])
    };
  });

  return {
    ...base,
    accounts: dedupeById([...base.accounts, ...wrapperState.accounts]),
    schools: dedupeById([...base.schools, ...wrapperState.schools]),
    topics: mergedTopics,
    lessons: mergedLessons,
    materials: [...materialsById.values()],
    auditEvents: dedupeById([...base.auditEvents, ...wrapperState.auditEvents])
  };
}

export async function fetchAccounts(): Promise<UserAccount[]> {
  if (hasWrapperApi()) {
    try {
      return await requestWrapperApi<UserAccount[]>("/users");
    } catch {
      // fall through to bootstrap
    }
  }
  const bootstrap = await fetchBootstrap();
  return wait(bootstrap.accounts);
}

export async function fetchSchools(): Promise<School[]> {
  if (hasWrapperApi()) {
    try {
      return await requestWrapperApi<School[]>("/schools");
    } catch {
      // fall through to bootstrap
    }
  }
  const bootstrap = await fetchBootstrap();
  return wait(bootstrap.schools);
}

export async function searchSchools(query: string): Promise<School[]> {
  if (!query.trim()) return [];
  if (hasWrapperApi()) {
    try {
      return await requestUnauthenticatedApi<School[]>(`/schools?search=${encodeURIComponent(query)}`);
    } catch {
      // fall through to bootstrap filter
    }
  }
  const bootstrap = await fetchBootstrap();
  const allSchools = await wait(bootstrap.schools);
  const lowerQuery = query.toLowerCase();
  return allSchools.filter((s: School) => s.name.toLowerCase().includes(lowerQuery));
}

export async function fetchSubjects(): Promise<Subject[]> {
  const bootstrap = await fetchBootstrap();
  return wait(bootstrap.subjects);
}

export async function fetchStudentProfile(studentId: string): Promise<StudentProfile> {
  if (!hasWrapperApi()) {
    throw new Error("Backend API not configured. Please set VITE_API_BASE_URL.");
  }
  return await requestWrapperApi<StudentProfile>(`/students/${encodeURIComponent(studentId)}/profile`);
}

export async function updateStudentSubjects(studentId: string, subjectIds: string[]): Promise<StudentProfile> {
  requireWrapperApi();
  return requestWrapperApi<StudentProfile>(
    `/students/${encodeURIComponent(studentId)}/subjects`,
    {
      method: "PUT",
      body: JSON.stringify({ subjectIds })
    }
  );
}

export async function fetchTopics(subjectId: string): Promise<Topic[]> {
  return requestWrapperApi<Topic[]>(`/subjects/${subjectId}/topics`);
}

export async function fetchLessons(subjectId: string): Promise<Lesson[]> {
  const bootstrap = await fetchBootstrap();
  return bootstrap.lessons.filter((lesson) => lesson.subjectId === subjectId);
}

export async function fetchStudyMaterials(subjectId: string): Promise<StudyMaterial[]> {
  const bootstrap = await fetchBootstrap();
  return bootstrap.materials.filter((material) => material.subjectId === subjectId);
}

export async function retrySyncOpenNotebook(materialId: string): Promise<{ materialId: string; status: "synced" | "failed" } | null> {
  if (hasWrapperApi()) {
    try {
      const result = await requestWrapperApi<{ materialId: string; status: string }>(
        `/study-materials/${encodeURIComponent(materialId)}/retry-sync`,
        { method: "POST" }
      );
      return result as { materialId: string; status: "synced" | "failed" };
    } catch {
      return null;
    }
  }
  return null;
}

export async function fetchQuizQuestions(
  lessonId: string,
  lessons?: Lesson[],
  topics?: Topic[],
  materials?: StudyMaterial[]
): Promise<QuizQuestion[]> {
  const questionSet = demoQuizQuestions.filter((question) => question.lessonId === lessonId);

  if (questionSet.length) {
    return wait(questionSet);
  }

  const bootstrap =
    lessons && topics && materials
      ? { lessons, topics, materials }
      : await fetchBootstrap();
  const lesson = bootstrap.lessons.find((item) => item.id === lessonId);

  if (!lesson) {
    return wait([]);
  }

  const topicLookup = new Map(bootstrap.topics.map((topic) => [topic.id, topic]));
  return wait(quizFromLesson(lesson, topicLookup, bootstrap.materials));
}

export type GenerateAdaptiveQuizOptions = {
  studentId: string;
  subjectId: string;
  maxQuestions?: number;
  topicId?: string;
  focusName?: string;
};

export async function startQuizGeneration(
  options: GenerateAdaptiveQuizOptions
): Promise<{ status: string }> {
  return requestWrapperApi<{ status: string }>(
    `/subjects/${encodeURIComponent(options.subjectId)}/generate-quiz`,
    {
      method: "POST",
      body: JSON.stringify({
        studentId: options.studentId,
        maxQuestions: options.maxQuestions,
        topicId: options.topicId,
        focusName: options.focusName,
      })
    }
  );
}

export function watchQuizViaSSE(
  studentId: string,
  subjectId: string,
  onCompleted: (questions: QuizQuestion[], quizId?: string) => void,
  onFailed: (error?: string) => void,
  timeoutMs: number = 180000,
): () => void {
  const token = getAuthToken();
  const { apiBaseUrl } = getConfig();
  if (!token || !apiBaseUrl) {
    onFailed('Not authenticated');
    return () => {};
  }

  const url = `${apiBaseUrl.replace(/\/$/, '')}/subjects/${encodeURIComponent(subjectId)}/quiz-events/${encodeURIComponent(studentId)}?token=${encodeURIComponent(token)}`;
  const source = new EventSource(url);
  let timedOut = false;

  const timer = setTimeout(() => {
    timedOut = true;
    source.close();
    onFailed('Quiz generation timed out');
  }, timeoutMs);

  source.addEventListener('quiz-completed', (e: MessageEvent) => {
    if (timedOut) return;
    clearTimeout(timer);
    source.close();
    const data = JSON.parse(e.data);
    onCompleted(data.questions, data.quizId);
  });

  source.addEventListener('quiz-failed', (e: MessageEvent) => {
    if (timedOut) return;
    clearTimeout(timer);
    source.close();
    const data = JSON.parse(e.data);
    onFailed(data.error || 'Generation failed');
  });

  source.addEventListener('error', () => {
    if (timedOut) return;
    clearTimeout(timer);
    source.close();
    onFailed('Connection lost');
  });

  return () => {
    clearTimeout(timer);
    source.close();
  };
}

export async function pollQuizStatus(
  studentId: string,
  subjectId: string,
  onUpdate: (status: 'pending' | 'generating' | 'completed' | 'failed' | 'idle', questions?: QuizQuestion[], error?: string) => void,
  intervalMs: number = 2000,
  timeoutMs: number = 180000,
): Promise<void> {
  const start = Date.now();
  const poll = async (): Promise<void> => {
    if (Date.now() - start > timeoutMs) {
      onUpdate('failed', undefined, 'Quiz generation timed out');
      return;
    }

    try {
      const result = await requestWrapperApi<{
        status: string;
        questions?: QuizQuestion[];
        error?: string;
      }>(`/subjects/${encodeURIComponent(subjectId)}/quiz-status/${encodeURIComponent(studentId)}`);

      if (result.status === 'completed') {
        onUpdate('completed', result.questions);
        return;
      }
      if (result.status === 'failed') {
        onUpdate('failed', undefined, result.error);
        return;
      }
      if (result.status === 'idle') {
        onUpdate('idle');
        return;
      }

      onUpdate(result.status as 'pending' | 'generating');
      setTimeout(poll, intervalMs);
    } catch {
      // Network error — retry
      setTimeout(poll, intervalMs);
    }
  };

  poll();
}

export async function fetchQuizById(studentId: string, quizId: string): Promise<any> {
  requireWrapperApi();
  return requestWrapperApi<any>(
    `/students/${encodeURIComponent(studentId)}/quizzes/${encodeURIComponent(quizId)}`
  );
}

export async function saveQuizProgress(
  studentId: string,
  quizId: string,
  answers: Record<string, string>,
): Promise<void> {
  requireWrapperApi();
  await requestWrapperApi<any>(
    `/students/${encodeURIComponent(studentId)}/quizzes/${encodeURIComponent(quizId)}/progress`,
    {
      method: "PATCH",
      body: JSON.stringify({ answers }),
    }
  );
}

export async function fetchQuizHistory(studentId: string): Promise<any[]> {
  requireWrapperApi();
  return requestWrapperApi<any[]>(
    `/students/${encodeURIComponent(studentId)}/quiz-history`
  );
}

export async function fetchStudentUnderstanding(
  studentId: string
): Promise<StudentUnderstandingProfile> {
  requireWrapperApi();
  return requestWrapperApi<StudentUnderstandingProfile>(
    `/student-profiles/${encodeURIComponent(studentId)}`
  );
}

export async function fetchRecommendedNextStep(
  studentId: string,
  subjectId: string
): Promise<RecommendedNextStep> {
  requireWrapperApi();
  return requestWrapperApi<RecommendedNextStep>(
    `/students/${encodeURIComponent(studentId)}/subjects/${encodeURIComponent(subjectId)}/recommended-next-step`
  );
}

export async function fetchChatHistory(_lessonId: string): Promise<ChatMessage[]> {
  const bootstrap = await fetchBootstrap();
  const lesson = bootstrap.lessons.find((item) => item.id === _lessonId);

  if (lesson?.sourceId) {
    const sessions = await proxyOpenNotebook<
      Array<{ id: string; updated?: string; created?: string }>
    >(`/sources/${lesson.sourceId}/chat/sessions`);
    const latest = [...sessions].sort((a, b) =>
      (b.updated || b.created || "").localeCompare(a.updated || a.created || "")
    )[0];

    if (latest?.id) {
      const session = await proxyOpenNotebook<{
        messages?: Array<{ role?: string; content?: string; created?: string }>;
      }>(`/sources/${lesson.sourceId}/chat/sessions/${latest.id}`);

      if (session.messages?.length) {
        return wait(
          session.messages.map((message, index) => ({
            id: `${latest.id}-${index}`,
            role: message.role === "assistant" ? "assistant" : "user",
            content: message.content || "",
            createdAt: message.created || new Date().toISOString()
          }))
        );
      }
    }
  }

  return wait([]);
}

export async function fetchStudentChatHistory(studentId: string, subjectId: string): Promise<ChatMessage[]> {
  requireWrapperApi();
  return requestWrapperApi<ChatMessage[]>(
    `/students/${encodeURIComponent(studentId)}/subjects/${encodeURIComponent(subjectId)}/chat-history`
  );
}

export async function fetchStudentChatSessions(studentId: string, subjectId: string) {
  requireWrapperApi();
  return requestWrapperApi<StudentChatSession[]>(
    `/students/${encodeURIComponent(studentId)}/subjects/${encodeURIComponent(subjectId)}/chat-sessions`
  );
}

export async function createStudentChatSession(studentId: string, subjectId: string, title?: string) {
  requireWrapperApi();
  return requestWrapperApi<StudentChatSession>(
    `/students/${encodeURIComponent(studentId)}/subjects/${encodeURIComponent(subjectId)}/chat-sessions`,
    {
      method: "POST",
      body: JSON.stringify({ title })
    }
  );
}

export async function fetchStudentChatSessionMessages(
  studentId: string,
  subjectId: string,
  chatSessionId: string,
): Promise<ChatMessage[]> {
  requireWrapperApi();
  return requestWrapperApi<ChatMessage[]>(
    `/students/${encodeURIComponent(studentId)}/subjects/${encodeURIComponent(subjectId)}/chat-sessions/${encodeURIComponent(chatSessionId)}/messages`
  );
}

export async function updateStudentChatSessionTitle(
  studentId: string,
  subjectId: string,
  chatSessionId: string,
  title: string,
) {
  requireWrapperApi();
  return requestWrapperApi<StudentChatSession>(
    `/students/${encodeURIComponent(studentId)}/subjects/${encodeURIComponent(subjectId)}/chat-sessions/${encodeURIComponent(chatSessionId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ title })
    }
  );
}

export async function deleteStudentChatSession(
  studentId: string,
  subjectId: string,
  chatSessionId: string,
) {
  requireWrapperApi();
  return requestWrapperApi<{ ok: boolean }>(
    `/students/${encodeURIComponent(studentId)}/subjects/${encodeURIComponent(subjectId)}/chat-sessions/${encodeURIComponent(chatSessionId)}`,
    { method: "DELETE" }
  );
}

export async function submitQuizAnswers(
  studentId: string,
  questions: QuizQuestion[],
  answers: Record<string, string>,
  quizId?: string
): Promise<StudentUnderstandingProfile> {
  requireWrapperApi();
  return requestWrapperApi<StudentUnderstandingProfile>(
    `/student-profiles/${encodeURIComponent(studentId)}/quiz-events`,
    {
      method: "POST",
      body: JSON.stringify({ questions, answers, quizId })
    }
  );
}

export async function sendChatMessage(
  studentId: string,
  subjectId: string,
  message: string,
  isFirstMessage: boolean = false,
  profile?: StudentUnderstandingProfile | null,
  chatSessionId?: string,
): Promise<ChatResponse & { interpretation?: InterpretationResult; intervention?: InterventionTrigger }> {
  const timeoutMs = 120000;

  return Promise.race([
    doSendChatMessage(studentId, subjectId, message, isFirstMessage, profile, chatSessionId),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('sendChatMessage timeout')), timeoutMs)
    )
  ]);
}

async function doSendChatMessage(
  studentId: string,
  subjectId: string,
  message: string,
  isFirstMessage: boolean = false,
  profile?: StudentUnderstandingProfile | null,
  chatSessionId?: string,
): Promise<ChatResponse & { interpretation?: InterpretationResult; intervention?: InterventionTrigger }> {
  const bootstrap = await fetchBootstrap();
  const topics = bootstrap.topics;
  const concepts = demoConcepts;
  
  const topicForSubject = topics.find(t => t.subjectId === subjectId);

  const interpretation = interpretStudentReasoning(
    message,
    {
      topicId: topicForSubject?.id,
      lessonId: undefined
    },
    topics,
    concepts
  );

  const intervention = shouldTriggerIntervention(interpretation, []);

  requireWrapperApi();
  const chatResult = await requestWrapperApi<{
    chatSession?: StudentChatSession;
    assistant: ChatMessage;
    updatedProfile: StudentUnderstandingProfile;
    tutoringDirective?: ChatResponse["tutoringDirective"];
    recommendedNextStep?: RecommendedNextStep;
  }>(
    chatSessionId
      ? `/students/${encodeURIComponent(studentId)}/subjects/${encodeURIComponent(subjectId)}/chat-sessions/${encodeURIComponent(chatSessionId)}/messages`
      : `/students/${encodeURIComponent(studentId)}/subjects/${encodeURIComponent(subjectId)}/chat`,
    {
      method: "POST",
      body: JSON.stringify({
        message,
        chatSessionId,
      })
    }
  );

  console.log('[API] Returning assistant response');

  return wait({
    assistant: chatResult.assistant,
    chatSession: chatResult.chatSession,
    updatedProfile: chatResult.updatedProfile,
    tutoringDirective: chatResult.tutoringDirective,
    recommendedNextStep: chatResult.recommendedNextStep,
    interpretation,
    intervention
  });
}

export async function ensureNotebookChatReady(
  studentId: string,
  subjectId: string,
  profile?: StudentUnderstandingProfile | null,
): Promise<{ sessionId: string | null; notebookId: string }> {
  const cacheKey = `${studentId}:${subjectId}`;
  const cached = notebookChatWarmCache[cacheKey];

  let notebookId = cached?.notebookId || "";
  if (!notebookId) {
    const nb = await fetchStudentNotebook(studentId, subjectId);
    if (!nb) {
      throw new Error("Open Notebook notebook is required for chat.");
    }
    notebookId = nb;
  }

  let sessionId: string | null = cached?.sessionId || null;
  if (!sessionId) {
    const payloads = [{ notebook_id: notebookId }, { notebookId }];

    let lastSessionError: unknown;
    for (const payload of payloads) {
      try {
        const response = await proxyOpenNotebook<ChatSessionResponse>("/chat/sessions", {
          method: "POST",
          body: JSON.stringify(payload)
        });
        const id = response.id || response.session_id;
        if (id) {
          sessionId = id;
          break;
        }
      } catch (error) {
        lastSessionError = error;
      }
    }

    if (!sessionId) {
      throw lastSessionError instanceof Error
        ? lastSessionError
        : new Error("Open Notebook chat session could not be created.");
    }
  }

  notebookChatWarmCache[cacheKey] = {
    notebookId,
    sessionId
  };

  if (sessionId && notebookId) {
    try {
      const bootstrap = await fetchBootstrap();
      const subject = bootstrap.subjects.find((s) => s.id === subjectId);
      const lowMasteryTopics = (profile?.mastery || [])
        .filter((m: any) => Number(m.masteryPercent || 0) < 50)
        .map((m: any) => ({ topic: m.topicName || m.topicId, masteryPercent: Number(m.masteryPercent || 0) }));

      await proxyOpenNotebook(`/notebooks/${encodeURIComponent(notebookId)}/context`, {
        method: "POST",
        body: JSON.stringify({
          notebook_id: notebookId,
          context_config: {
            activeSubject: {
              id: subjectId,
              name: subject?.name || subjectId
            },
            ...(profile
              ? {
                  studentProfile: {
                    studentName: profile.studentName,
                    mastery: profile.mastery,
                    gapSignals: profile.gapSignals,
                    conceptMastery: profile.conceptMastery,
                    recommendedFocus: profile.recommendedFocus
                  }
                }
              : {}),
            tutorInstructions: {
              role: "You are a helpful tutor. Default to giving clear, concise direct answers.",
              guidelines: [
                `The student is currently working in ${subject?.name || subjectId}.`,
                "Give the student a clear, concise direct answer to their question.",
                "Focus on topics where the student has LOW mastery (below 50%) and active gap signals.",
                lowMasteryTopics.length > 0 ? `The student's weakest topics are: ${lowMasteryTopics.map((t: any) => `${t.topic} (mastery ${t.masteryPercent.toFixed(0)}%)`).join(', ')}. Prioritize these.` : null,
                "Reference specific concepts the student has struggled with (from gapSignals) when relevant.",
                "Keep responses concise and encourage the student.",
              ].filter(Boolean)
            }
          }
        })
      });
    } catch {
      // non-blocking
    }
  }

  return { sessionId, notebookId };
}

function createStreamingXHR(
  url: string,
  body: string,
  headers: Record<string, string>,
  onChunk: (chunk: string) => void,
  onComplete: (fullContent: string) => void
): Promise<{ status: number; body: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);

    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    let buffer = '';

    xhr.onprogress = () => {
      const newData = (xhr.responseText || '').slice(buffer.length);
      buffer += newData;
      onChunk(newData);
    };

    xhr.onload = () => {
      onComplete(buffer);
      resolve({ status: xhr.status, body: buffer });
    };

    xhr.onerror = () => reject(new Error('XHR error'));
    xhr.timeout = 120000;
    xhr.ontimeout = () => reject(new Error('XHR timeout'));

    xhr.send(body);
  });
}

function parseSSEChunk(chunk: string): string | null {
  const lines = chunk.split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('data:')) {
      const data = trimmed.slice(5).trim();
      if (data === '[DONE]') {
        return null;
      }
      try {
        const parsed = JSON.parse(data);
        const content = parsed.answer || parsed.response || parsed.content || parsed.message || parsed.delta || parsed.text;
        if (content) return content;
      } catch {
        if (data) return data;
      }
    }
  }
  return null;
}

async function executeOpenNotebookChat(sessionId: string, lesson: Lesson, message: string, context?: Record<string, unknown>) {
  const proxyUrl = getProxyUrl();

  if (lesson.sourceId) {
    try {
      const chatUrl = `${proxyUrl}/sources/${lesson.sourceId}/chat/sessions/${sessionId}/messages`;

      const response = await fetch(chatUrl, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/plain'
        },
        body: JSON.stringify({
          message,
          ...(context ? { context } : {})
        })
      });

      if (response.ok) {
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          const json = await response.json();
          return json as OpenNotebookChatResponse;
        }
      }
    } catch (e) {
      console.log('[API] Source chat endpoint failed:', e instanceof Error ? e.message : String(e));
    }

    // Fallback: use streaming XHR against the proxy chat execute endpoint
    const directContextPayload = context || { topic: lesson.topicId, lesson: lesson.title };

    try {
      const chatExecuteUrl = `${proxyUrl}/chat/execute`;
      const bodyJson = JSON.stringify({
        session_id: sessionId,
        notebook_id: lesson.notebookId,
        source_id: lesson.sourceId,
        prompt: message,
        context: directContextPayload,
        model_override: "model:3l6acshue2oglfkgh3ya"
      });

      let streamedContent = '';

      const xhrResult = await createStreamingXHR(
        chatExecuteUrl,
        bodyJson,
        { 'Content-Type': 'application/json' },
        (chunk) => {
          const parsed = parseSSEChunk(chunk);
          if (parsed) {
            streamedContent += parsed;
          }
        },
        () => {}
      );

      if (xhrResult.status >= 200 && xhrResult.status < 300) {
        if (streamedContent) {
          return { content: streamedContent };
        }
        try {
          const chatResult = JSON.parse(xhrResult.body);
          const messages = chatResult.messages || [];
          const aiMessage = messages.find((m: any) => m.type === 'ai');
          if (aiMessage) {
            return { content: aiMessage.content };
          }
        } catch {
          return { content: xhrResult.body };
        }
      }
    } catch (e) {
      console.log('[API] Streaming chat failed:', e instanceof Error ? e.message : String(e));
    }
  }

  // Fallback: try alternate payload shapes via proxy
  const payloads = [
    { session_id: sessionId, notebook_id: lesson.notebookId, message, source_id: lesson.sourceId, ...(context ? { context } : {}) },
    { sessionId, notebookId: lesson.notebookId, message, sourceId: lesson.sourceId, ...(context ? { context } : {}) },
    { session_id: sessionId, notebook_id: lesson.notebookId, prompt: message, source_id: lesson.sourceId, ...(context ? { context } : {}) },
  ];

  let lastError: unknown;
  const contextPayload = context || { topic: lesson.topicId, lesson: lesson.title };

  for (const payload of payloads) {
    try {
      const result = await proxyOpenNotebook<OpenNotebookChatResponse>("/chat/execute", {
        method: "POST",
        body: JSON.stringify({ ...payload, context: contextPayload, model_override: "model:3l6acshue2oglfkgh3ya" })
      });
      return result;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error ? lastError : new Error("Open Notebook chat failed.");
}

function parseSseOrTextResponse(text: string): OpenNotebookChatResponse | null {
  const dataLines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trim())
    .filter(Boolean);

  for (let index = dataLines.length - 1; index >= 0; index -= 1) {
    const line = dataLines[index];
    if (line === "[DONE]") {
      continue;
    }

    try {
      const parsed = JSON.parse(line) as Record<string, unknown>;
      const content =
        (typeof parsed.answer === "string" && parsed.answer) ||
        (typeof parsed.response === "string" && parsed.response) ||
        (typeof parsed.content === "string" && parsed.content) ||
        (typeof parsed.message === "string" && parsed.message) ||
        (typeof parsed.delta === "string" && parsed.delta) ||
        (typeof parsed.text === "string" && parsed.text);

      if (content) {
        return { content };
      }
    } catch {
      if (line) {
        return { content: line };
      }
    }
  }

  return text.trim() ? { content: text.trim() } : null;
}

export async function fetchApiHealth(): Promise<ApiHealth[]> {
  const bootstrap = await fetchBootstrap();
  return wait(bootstrap.apiHealth);
}

export async function fetchAuditEvents(): Promise<AuditEvent[]> {
  if (hasWrapperApi()) {
    try {
      return await requestWrapperApi<AuditEvent[]>("/audit-events");
    } catch {
      // fall through to bootstrap
    }
  }
  const bootstrap = await fetchBootstrap();
  return wait(bootstrap.auditEvents);
}

export async function fetchGovernanceSummary(
  lessonId: string
): Promise<GovernanceSummary | null> {
  const bootstrap = await fetchBootstrap();
  const lesson = bootstrap.lessons.find((item) => item.id === lessonId);

  if (!lesson) {
    return wait(null);
  }

  const topic = bootstrap.topics.find((item) => item.id === lesson.topicId);
  const approvedMaterialCount = bootstrap.materials.filter(
    (material) =>
      lesson.sourceMaterialIds.includes(material.id) && material.sourceAuthority === "approved"
  ).length;
  const transcriptReady = Boolean(lesson.transcript?.trim());
  const summaryReady = Boolean(lesson.summary?.highLevelSummary?.trim());
  const prerequisiteCount = topic?.prerequisiteTopicIds.length || 0;
  const lessonDependencyCount = lesson.lessonDependencyIds?.length || 0;

  return wait({
    lessonId,
    publicationStatus: lesson.publicationStatus || "draft",
    approvedMaterialCount,
    prerequisiteCount,
    lessonDependencyCount,
    transcriptReady,
    summaryReady,
    canPublish:
      transcriptReady && summaryReady && approvedMaterialCount > 0 && lessonDependencyCount > 0
  });
}

export async function createTopic(input: Omit<Topic, "id"> & { id?: string }) {
  const wrapperState = readWrapperState();
  const topic: Topic = {
    ...input,
    id: input.id || `topic-${slugify(input.name)}`
  };
  wrapperState.topicOverrides[topic.id] = topic;
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: "create_topic",
    targetType: "topic",
    targetId: topic.id,
    summary: `Created topic ${topic.name}.`
  });
  writeWrapperState(wrapperState);
  await syncRolesAndAuditToOpenNotebook();
  return cloneTopic(topic);
}

export async function updateTopic(topicId: string, patch: Partial<Omit<Topic, "id">>) {
  const bootstrap = await fetchBootstrap();
  const wrapperState = readWrapperState();
  const currentTopic = bootstrap.topics.find((topic) => topic.id === topicId);
  if (!currentTopic) {
    throw new Error("Topic not found.");
  }
  wrapperState.topicOverrides[topicId] = {
    ...currentTopic,
    ...wrapperState.topicOverrides[topicId],
    ...patch
  };
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: "update_topic",
    targetType: "topic",
    targetId: topicId,
    summary: `Updated topic ${currentTopic.name}.`
  });
  writeWrapperState(wrapperState);

  await syncGovernanceNotesForTopic(topicId);
  await syncRolesAndAuditToOpenNotebook();
  return wait(cloneTopic(wrapperState.topicOverrides[topicId] as Topic));
}

export async function updateLessonDependencies(
  lessonId: string,
  lessonDependencyIds: string[]
) {
  const wrapperState = readWrapperState();
  const governance = await ensureLessonGovernance(lessonId);
  wrapperState.governanceByLessonId[lessonId] = {
    ...governance,
    lessonDependencyIds: dedupeStrings(lessonDependencyIds)
  };
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: "update_lesson_dependencies",
    targetType: "lesson",
    targetId: lessonId,
    summary: `Updated lesson dependency map for ${lessonId}.`
  });
  writeWrapperState(wrapperState);

  await syncGovernanceNoteForLesson(lessonId);
  await syncRolesAndAuditToOpenNotebook();
  const bootstrap = await fetchBootstrap();
  return wait(cloneLesson(bootstrap.lessons.find((lesson) => lesson.id === lessonId)!));
}

export async function createStudyMaterial(
  input: Omit<StudyMaterial, "id"> & {
    id?: string;
    lessonId?: string;
    file?: File;
  }
) {
  if (hasWrapperApi() && input.file) {
    const classification = classifyMaterialFile(input.file.name, input.file.type || "");
    const material = await requestWrapperApi<StudyMaterial>("/study-materials", {
      method: "POST",
      body: JSON.stringify({
        subjectId: input.subjectId,
        topicIds: input.topicIds,
        title: input.title,
        sourceAuthority: input.sourceAuthority,
        citation: input.citation,
        lessonId: input.lessonId,
        materialType: classification.materialType,
        excerpt: input.excerpt,
        file: {
          fileName: input.file.name,
          mimeType: input.file.type || "application/octet-stream",
          sizeBytes: input.file.size,
          base64Content: await fileToBase64(input.file)
        }
      })
    });
  
    return withApiAssetUrl(material);
  }

  const wrapperState = readWrapperState();
  const assetClassification = input.file
    ? classifyMaterialFile(input.file.name, input.file.type || "")
    : null;
  const { file, ...materialInput } = input;
  const material: StudyMaterial = {
    ...materialInput,
    id: input.id || `material-${Date.now()}`,
    materialType: assetClassification?.materialType || input.materialType,
    ingestionStatus: file ? "ready" : input.ingestionStatus,
    ingestionNotes: file
      ? "Validated and catalogued locally. Wrapper upload is unavailable, so the file is not stored on the server."
      : input.ingestionNotes,
    asset: file
      ? {
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          sizeBytes: file.size,
          uploadedAt: new Date().toISOString(),
          fileCategory: assetClassification!.fileCategory
        }
      : input.asset
  };
  if (input.lessonId) {
    const governance = await ensureLessonGovernance(input.lessonId);
    wrapperState.governanceByLessonId[input.lessonId] = {
      ...governance,
      managedMaterials: dedupeById([...(governance.managedMaterials || []), material]),
      approvedMaterialIds:
        material.sourceAuthority === "approved"
          ? dedupeStrings([...governance.approvedMaterialIds, material.id])
          : governance.approvedMaterialIds
    };
  } else {
    wrapperState.materials = dedupeById([...wrapperState.materials, material]);
  }
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: "create_material",
    targetType: "material",
    targetId: material.id,
    summary: `Added study material ${material.title}.`
  });
  writeWrapperState(wrapperState);

  if (input.lessonId) {
    await syncGovernanceNoteForLesson(input.lessonId);
  }
  await syncRolesAndAuditToOpenNotebook();
  return wait(cloneMaterial(material));
}

export async function updateStudyMaterial(
  materialId: string,
  patch: Partial<Omit<StudyMaterial, "id">> & { lessonId?: string }
) {
  if (hasWrapperApi()) {
    try {
      const updated = await requestWrapperApi<StudyMaterial>(
        `/study-materials/${encodeURIComponent(materialId)}`,
        {
          method: "PUT",
          body: JSON.stringify(patch)
        }
      );
    
      return withApiAssetUrl(updated);
    } catch {
      // fall through to note-backed/local
    }
  }

  const bootstrap = await fetchBootstrap();
  const wrapperState = readWrapperState();
  const current = bootstrap.materials.find((material) => material.id === materialId);
  if (!current) {
    throw new Error("Material not found.");
  }
  const nextMaterial = { ...current, ...patch };
  const targetLessonId = patch.lessonId || bootstrap.lessons.find((lesson) => lesson.sourceMaterialIds.includes(materialId))?.id;
  if (targetLessonId) {
    const governance = await ensureLessonGovernance(targetLessonId);
    wrapperState.governanceByLessonId[targetLessonId] = {
      ...governance,
      managedMaterials: dedupeById([
        ...(governance.managedMaterials || []).filter((material) => material.id !== materialId),
        nextMaterial
      ]),
      approvedMaterialIds:
        nextMaterial.sourceAuthority === "approved"
          ? dedupeStrings([...governance.approvedMaterialIds, materialId])
          : governance.approvedMaterialIds.filter((id) => id !== materialId)
    };
  }
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: "update_material",
    targetType: "material",
    targetId: materialId,
    summary: `Updated study material ${current.title}.`
  });
  writeWrapperState(wrapperState);

  if (targetLessonId) {
    await syncGovernanceNoteForLesson(targetLessonId);
  }
  await syncRolesAndAuditToOpenNotebook();
  return wait(cloneMaterial(nextMaterial));
}

export async function deleteStudyMaterial(materialId: string) {
  if (hasWrapperApi()) {
    const { apiBaseUrl } = getConfig();
    const authToken = getAuthToken();
    const headers: Record<string, string> = {};
    if (authToken) {
      headers["Authorization"] = `Bearer ${authToken}`;
    }
    const response = await fetch(`${apiBaseUrl}/study-materials/${encodeURIComponent(materialId)}`, {
      method: "DELETE",
      headers
    });
  if (!response.ok) {
    const detail = await response.text();
    let errorMessage: string;
    try {
      const parsed = JSON.parse(detail);
      errorMessage = parsed.message || detail;
    } catch {
      errorMessage = detail || `Wrapper API request failed with ${response.status}`;
    }
    throw new Error(errorMessage);
  }
  
    return;
  }

  const wrapperState = readWrapperState();
  for (const lessonId of Object.keys(wrapperState.governanceByLessonId)) {
    wrapperState.governanceByLessonId[lessonId] = {
      ...wrapperState.governanceByLessonId[lessonId],
      managedMaterials: (wrapperState.governanceByLessonId[lessonId].managedMaterials || []).filter(
        (material) => material.id !== materialId
      ),
      approvedMaterialIds: wrapperState.governanceByLessonId[lessonId].approvedMaterialIds.filter(
        (id) => id !== materialId
      )
    };
  }
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: "delete_material",
    targetType: "material",
    targetId: materialId,
    summary: `Deleted study material ${materialId}.`
  });
  writeWrapperState(wrapperState);

  await syncAllGovernanceNotesToOpenNotebook();
  await syncRolesAndAuditToOpenNotebook();
  return wait(undefined);
}

export async function bulkDeleteStudyMaterials(materialIds: string[]) {
  if (hasWrapperApi()) {
    const { apiBaseUrl } = getConfig();
    const authToken = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json"
    };
    if (authToken) {
      headers["Authorization"] = `Bearer ${authToken}`;
    }
    const response = await fetch(`${apiBaseUrl}/study-materials/bulk-delete`, {
      method: "POST",
      headers,
      body: JSON.stringify({ materialIds })
    });
  if (!response.ok) {
    const detail = await response.text();
    let errorMessage: string;
    try {
      const parsed = JSON.parse(detail);
      errorMessage = parsed.message || detail;
    } catch {
      errorMessage = detail || `Wrapper API request failed with ${response.status}`;
    }
    throw new Error(errorMessage);
  }
    return;
  }

  const wrapperState = readWrapperState();
  const idSet = new Set(materialIds);
  for (const lessonId of Object.keys(wrapperState.governanceByLessonId)) {
    wrapperState.governanceByLessonId[lessonId] = {
      ...wrapperState.governanceByLessonId[lessonId],
      managedMaterials: (wrapperState.governanceByLessonId[lessonId].managedMaterials || []).filter(
        (material) => !idSet.has(material.id)
      ),
      approvedMaterialIds: wrapperState.governanceByLessonId[lessonId].approvedMaterialIds.filter(
        (id) => !idSet.has(id)
      )
    };
  }
  for (const materialId of materialIds) {
    appendAuditEvent(wrapperState, {
      actorId: "user-superadmin",
      actorName: "Ava Naidoo",
      action: "delete_material",
      targetType: "material",
      targetId: materialId,
      summary: `Deleted study material ${materialId}.`
    });
  }
  writeWrapperState(wrapperState);

  await syncAllGovernanceNotesToOpenNotebook();
  await syncRolesAndAuditToOpenNotebook();
  return wait(undefined);
}

export async function assignUserRole(userId: string, role: UserRole) {
  if (hasWrapperApi()) {
    try {
      return await requestWrapperApi<UserAccount>(
        `/users/${encodeURIComponent(userId)}/role`,
        {
          method: "PUT",
          body: JSON.stringify({ role })
        }
      );
    } catch {
      // fall through to note-backed/local
    }
  }

  const wrapperState = readWrapperState();
  wrapperState.accounts = wrapperState.accounts.map((account) =>
    account.id === userId ? { ...account, role } : account
  );
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: "assign_role",
    targetType: "account",
    targetId: userId,
    summary: `Assigned role ${role} to ${userId}.`
  });
  writeWrapperState(wrapperState);

  await syncRolesAndAuditToOpenNotebook();
  return wait(cloneAccount(wrapperState.accounts.find((account) => account.id === userId)!));
}

export async function createUserAccount(input: {
  fullName: string;
  email: string;
  role: UserRole;
  organizationName: string;
}) {
  if (hasWrapperApi()) {
    try {
      return await requestWrapperApi<UserAccount>("/users", {
        method: "POST",
        body: JSON.stringify(input)
      });
    } catch {
      // fall through to note-backed/local
    }
  }

  const wrapperState = readWrapperState();
  const fullName = input.fullName.trim();
  const email = input.email.trim().toLowerCase();
  const organizationName = input.organizationName.trim();

  if (!fullName || !email || !organizationName) {
    throw new Error("Full name, email, and school are required.");
  }

  if (wrapperState.accounts.some((account) => account.email.toLowerCase() === email)) {
    throw new Error("An account with that email already exists.");
  }

  const account: UserAccount = {
    id: `user-${slugify(`${fullName}-${Date.now()}`)}`,
    fullName,
    email,
    role: input.role,
    organizationName
  };

  wrapperState.accounts = [...wrapperState.accounts, account].sort((left, right) =>
    left.fullName.localeCompare(right.fullName)
  );
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: "create_account",
    targetType: "account",
    targetId: account.id,
    summary: `Created ${account.role} account for ${account.fullName}.`
  });
  writeWrapperState(wrapperState);

  await syncRolesAndAuditToOpenNotebook();
  return wait(cloneAccount(account));
}

export async function updateUserOrganization(
  userId: string,
  organizationName: string,
  actor?: { actorId: string; actorName: string }
) {
  if (hasWrapperApi()) {
    try {
      return await requestWrapperApi<UserAccount>(
        `/users/${encodeURIComponent(userId)}/organization`,
        {
          method: "PUT",
          body: JSON.stringify({ organizationName, ...actor })
        }
      );
    } catch {
      // fall through to note-backed/local
    }
  }

  const wrapperState = readWrapperState();
  const trimmedOrganizationName = organizationName.trim();
  if (!trimmedOrganizationName) {
    throw new Error("Organization name is required.");
  }

  wrapperState.accounts = wrapperState.accounts.map((account) =>
    account.id === userId ? { ...account, organizationName: trimmedOrganizationName } : account
  );
  appendAuditEvent(wrapperState, {
    actorId: actor?.actorId || "user-superadmin",
    actorName: actor?.actorName || "Ava Naidoo",
    action: "update_organization",
    targetType: "account",
    targetId: userId,
    summary: `Updated organization to ${trimmedOrganizationName} for ${userId}.`
  });
  writeWrapperState(wrapperState);

  await syncRolesAndAuditToOpenNotebook();
  return wait(cloneAccount(wrapperState.accounts.find((account) => account.id === userId)!));
}

export async function createSchool(input: {
  name: string;
  shortCode?: string;
  contactEmail?: string;
  actor?: {
    actorId: string;
    actorName: string;
  };
}) {
  if (hasWrapperApi()) {
    try {
      return await requestWrapperApi<School>("/schools", {
        method: "POST",
        body: JSON.stringify(input)
      });
    } catch {
      // fall through to note-backed/local
    }
  }

  const wrapperState = readWrapperState();
  const trimmedName = input.name.trim();
  const trimmedCode = (input.shortCode?.trim() || shortCodeFromName(trimmedName)).toUpperCase();

  if (!trimmedName || !trimmedCode) {
    throw new Error("School name and short code are required.");
  }

  const school: School = {
    id: `school-${slugify(trimmedName)}`,
    name: trimmedName,
    shortCode: trimmedCode,
    contactEmail: input.contactEmail?.trim() || undefined,
    createdAt: new Date().toISOString()
  };

  wrapperState.schools = dedupeById([school, ...wrapperState.schools]);
  appendAuditEvent(wrapperState, {
    actorId: input.actor?.actorId || "user-superadmin",
    actorName: input.actor?.actorName || "Ava Naidoo",
    action: "create_school",
    targetType: "school",
    targetId: school.id,
    summary: `Created school ${school.name}.`
  });
  writeWrapperState(wrapperState);

  await syncRolesAndAuditToOpenNotebook();
  return wait(structuredClone(school));
}

export async function deleteSchool(schoolId: string) {
  if (hasWrapperApi()) {
    try {
      await requestWrapperApi<{ ok: boolean }>(`/schools/${encodeURIComponent(schoolId)}`, {
        method: "DELETE"
      });
    
      return wait(undefined);
    } catch {
      // fall through to note-backed/local
    }
  }

  const wrapperState = readWrapperState();
  const school = wrapperState.schools.find((item) => item.id === schoolId);
  if (!school) {
    throw new Error("School not found.");
  }

  const linkedAccounts = wrapperState.accounts.filter(
    (account) => account.organizationName === school.name
  );
  if (linkedAccounts.length > 0) {
    throw new Error("This school still has accounts assigned to it and cannot be deleted.");
  }

  wrapperState.schools = wrapperState.schools.filter((item) => item.id !== schoolId);
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: "delete_school",
    targetType: "school",
    targetId: school.id,
    summary: `Deleted school ${school.name}.`
  });
  writeWrapperState(wrapperState);

  await syncRolesAndAuditToOpenNotebook();
  return wait(undefined);
}

export async function createSubject(input: {
  name: string;
  description?: string;
}) {
  if (hasWrapperApi()) {
    try {
      return await requestWrapperApi<Subject>("/subjects", {
        method: "POST",
        body: JSON.stringify(input)
      });
    } catch {
      // fall through to local
    }
  }

  const wrapperState = readWrapperState();
  const trimmedName = input.name.trim();

  if (!trimmedName) {
    throw new Error("Subject name is required.");
  }

  const subject: Subject = {
    id: `subject-${slugify(trimmedName)}`,
    name: trimmedName,
    description: input.description?.trim() || ""
  };

  wrapperState.subjects = dedupeById([subject, ...wrapperState.subjects]);
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: "create_subject",
    targetType: "subject",
    targetId: subject.id,
    summary: `Created subject ${subject.name}.`
  });
  writeWrapperState(wrapperState);
  return wait(structuredClone(subject));
}

export async function updateSubject(
  subjectId: string,
  patch: Partial<Omit<Subject, "id">>
) {
  if (hasWrapperApi()) {
    try {
      return await requestWrapperApi<Subject>(`/subjects/${encodeURIComponent(subjectId)}`, {
        method: "PUT",
        body: JSON.stringify(patch)
      });
    } catch {
      // fall through to local
    }
  }

  const wrapperState = readWrapperState();
  const subject = wrapperState.subjects.find((item) => item.id === subjectId);
  if (!subject) {
    throw new Error("Subject not found.");
  }

  const updated: Subject = {
    ...subject,
    ...patch,
    id: subject.id
  };

  wrapperState.subjects = dedupeById([updated, ...wrapperState.subjects]);
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: "update_subject",
    targetType: "subject",
    targetId: subject.id,
    summary: `Updated subject ${updated.name}.`
  });
  writeWrapperState(wrapperState);
  return wait(structuredClone(updated));
}

export async function deleteSubject(subjectId: string) {
  if (hasWrapperApi()) {
    try {
      await requestWrapperApi<{ ok: boolean }>(`/subjects/${encodeURIComponent(subjectId)}`, {
        method: "DELETE"
      });
    } catch {
      // fall through to local
    }
  }

  const wrapperState = readWrapperState();
  const subject = wrapperState.subjects.find((item) => item.id === subjectId);
  if (!subject) {
    throw new Error("Subject not found.");
  }

  const linkedTopics = wrapperState.topics.filter((topic) => topic.subjectId === subjectId);
  if (linkedTopics.length > 0) {
    throw new Error("This subject still has topics assigned to it. Delete topics first.");
  }

  wrapperState.subjects = wrapperState.subjects.filter((item) => item.id !== subjectId);
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: "delete_subject",
    targetType: "subject",
    targetId: subject.id,
    summary: `Deleted subject ${subject.name}.`
  });
  writeWrapperState(wrapperState);
  return wait(undefined);
}

export async function setLessonPublication(
  lessonId: string,
  publicationStatus: "draft" | "published",
  publishingNotes: string
) {
  const wrapperState = readWrapperState();
  const governance = await ensureLessonGovernance(lessonId);
  wrapperState.governanceByLessonId[lessonId] = {
    ...governance,
    publicationStatus,
    publishingNotes
  };
  appendAuditEvent(wrapperState, {
    actorId: "user-superadmin",
    actorName: "Ava Naidoo",
    action: publicationStatus === "published" ? "publish_lesson" : "draft_lesson",
    targetType: "lesson",
    targetId: lessonId,
    summary:
      publicationStatus === "published"
        ? `Published lesson ${lessonId} to students.`
        : `Moved lesson ${lessonId} back to draft.`
  });
  writeWrapperState(wrapperState);

  await syncGovernanceNoteForLesson(lessonId);
  await syncRolesAndAuditToOpenNotebook();
  const bootstrap = await fetchBootstrap();
  return wait(cloneLesson(bootstrap.lessons.find((lesson) => lesson.id === lessonId)!));
}

export function getRoleWorkspaces(role: UserRole) {
  if (role === "superadmin") {
    return ["schools", "subjects", "materials", "topic-graph", "accounts", "api-status"] as const;
  }

  return ["understanding-map", "practice", "chat", "materials"] as const;
}

export function getConfiguredApiBaseLabel() {
  const { apiBaseUrl } = getConfig();
  return apiBaseUrl || "Demo API mode";
}

function dedupeGapSignals(gapSignals: GapSignal[]) {
  const seen = new Map<string, GapSignal>();

  for (const gap of gapSignals) {
    seen.set(
      `${gap.signalSource}-${gap.topicId}-${gap.conceptId || "topic"}-${gap.evidence}`,
      gap
    );
  }

  return [...seen.values()];
}

function inferConceptMastery(
  current: StudentUnderstandingProfile,
  newSignals: GapSignal[]
): StudentUnderstandingProfile["conceptMastery"] {
  const conceptLookup = new Map(demoConcepts.map((concept) => [concept.id, concept]));
  const topicLookup = new Map(demoTopics.map((topic) => [topic.id, topic]));
  const next = current.conceptMastery.length
    ? structuredClone(current.conceptMastery)
    : demoConcepts.map((concept) => ({
        conceptId: concept.id,
        conceptName: concept.name,
        topicId: concept.topicId,
        topicName: topicLookup.get(concept.topicId)?.name || concept.topicId,
        confidence: 0.82,
        assumedUnderstood: true,
        evidence: "No negative evidence yet. The system assumes understanding until a gap is observed."
      }));

  for (const signal of newSignals) {
    if (!signal.conceptId) {
      continue;
    }

    const record = next.find((item) => item.conceptId === signal.conceptId);
    const concept = conceptLookup.get(signal.conceptId);
    const delta = signal.severity === "high" ? 0.22 : signal.severity === "medium" ? 0.15 : 0.08;

    if (record) {
      record.confidence = Math.max(0.2, Number((record.confidence - delta).toFixed(2)));
      record.assumedUnderstood = false;
      record.evidence = signal.evidence;
    } else if (concept) {
      next.push({
        conceptId: concept.id,
        conceptName: concept.name,
        topicId: concept.topicId,
        topicName: topicLookup.get(concept.topicId)?.name || concept.topicId,
        confidence: Math.max(0.2, Number((0.82 - delta).toFixed(2))),
        assumedUnderstood: false,
        evidence: signal.evidence
      });
    }
  }

  return next.sort((left, right) => left.confidence - right.confidence);
}

function inferRecommendedFocus(
  conceptMastery: StudentUnderstandingProfile["conceptMastery"],
  gapSignals: GapSignal[]
): StudentUnderstandingProfile["recommendedFocus"] {
  const byConcept = new Map<string, GapSignal>();
  for (const signal of gapSignals) {
    if (!signal.conceptId) {
      continue;
    }
    const current = byConcept.get(signal.conceptId);
    if (!current || (signal.confidence || 0) > (current.confidence || 0)) {
      byConcept.set(signal.conceptId, signal);
    }
  }

  return conceptMastery
    .filter((record) => record.confidence < 0.7)
    .map((record) => {
      const signal = byConcept.get(record.conceptId);
      return {
        conceptId: record.conceptId,
        conceptName: record.conceptName,
        topicId: record.topicId,
        topicName: record.topicName,
        reason:
          signal?.evidence ||
          "Recent interactions suggest this concept should be revisited before deeper topic work continues.",
        confidence: Number((1 - record.confidence).toFixed(2)),
        supportsUnderstandingOfTopicId: signal?.relatedTopicId
      };
    })
    .sort((left, right) => right.confidence - left.confidence)
    .slice(0, 4);
}

function recalculateUnderstandingProfile(
  current: StudentUnderstandingProfile,
  inferredGaps: GapSignal[],
  nextInferredFrom: StudentUnderstandingProfile["inferredFrom"]
): StudentUnderstandingProfile {
  const nextGapSignals = dedupeGapSignals([...inferredGaps, ...current.gapSignals]);
  const nextConceptMastery = inferConceptMastery(current, inferredGaps);
  const topicSignalCounts = new Map<string, number>();

  for (const signal of nextGapSignals) {
    topicSignalCounts.set(signal.topicId, (topicSignalCounts.get(signal.topicId) || 0) + 1);
  }

  return {
    ...current,
    gapSignals: nextGapSignals,
    conceptMastery: nextConceptMastery,
    recommendedFocus: inferRecommendedFocus(nextConceptMastery, nextGapSignals),
    mastery: current.mastery.map((record) => {
      const hitCount = topicSignalCounts.get(record.topicId) || 0;
      return hitCount
        ? {
            ...record,
            masteryPercent: Math.max(35, record.masteryPercent - hitCount * 6),
            evidence: `${record.evidence} ${hitCount} recent evidence signal(s) indicate a prerequisite concept may be unstable.`
          }
        : record;
    }),
    inferredFrom: nextInferredFrom
  };
}

function dedupeById<T extends { id: string }>(items: T[]) {
  const seen = new Map<string, T>();

  for (const item of items) {
    seen.set(item.id, item);
  }

  return [...seen.values()];
}

function dedupeStrings(values: string[]) {
  return [...new Set(values)];
}

function cloneBootstrap(value: AppBootstrap): AppBootstrap {
  return structuredClone(value);
}

function cloneTopic(topic: Topic): Topic {
  return structuredClone(topic);
}

function cloneMaterial(material: StudyMaterial): StudyMaterial {
  return structuredClone(material);
}

function cloneAccount(account: UserAccount): UserAccount {
  return structuredClone(account);
}

function cloneLesson(lesson: Lesson): Lesson {
  return structuredClone(lesson);
}

function appendAuditEvent(
  wrapperState: WrapperState,
  input: Omit<AuditEvent, "id" | "occurredAt">
) {
  wrapperState.auditEvents = [
    {
      ...input,
      id: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      occurredAt: new Date().toISOString()
    },
    ...wrapperState.auditEvents
  ].slice(0, 100);
}

async function ensureLessonGovernance(lessonId: string) {
  const bootstrap = await fetchBootstrap();
  const lesson = bootstrap.lessons.find((item) => item.id === lessonId);
  const topic = bootstrap.topics.find((item) => item.id === lesson?.topicId);

  if (!lesson || !topic) {
    throw new Error("Lesson governance target not found.");
  }

  return {
    lessonId,
    topicId: lesson.topicId,
    understandingGoal: topic.understandingGoal,
    prerequisiteTopicIds: topic.prerequisiteTopicIds,
    lessonDependencyIds: lesson.lessonDependencyIds || [],
    publicationStatus: lesson.publicationStatus || "draft",
    publishingNotes: lesson.publishingNotes || "",
    approvedMaterialIds: bootstrap.materials
      .filter((material) => lesson.sourceMaterialIds.includes(material.id))
      .filter((material) => material.sourceAuthority === "approved")
      .map((material) => material.id),
    managedMaterials: bootstrap.materials.filter((material) =>
      lesson.sourceMaterialIds.includes(material.id)
    )
  };
}

async function syncGovernanceNoteForLesson(lessonId: string) {
  const bootstrap = await fetchBootstrap();
  const wrapperState = readWrapperState();
  const lesson = bootstrap.lessons.find((item) => item.id === lessonId);
  if (!lesson?.notebookId) {
    return;
  }

  const governance =
    wrapperState.governanceByLessonId[lessonId] || (await ensureLessonGovernance(lessonId));
  await upsertOpenNotebookNote(
    lesson.notebookId,
    governanceNoteTitle(lessonId),
    JSON.stringify(
      {
        kind: "classroom-governance",
        lessonId,
        governance
      } satisfies GovernanceNoteDocument,
      null,
      2
    )
  );

  await syncSourceTopics(lessonId);
}

async function syncGovernanceNotesForTopic(topicId: string) {
  const bootstrap = await fetchBootstrap();
  const lessons = bootstrap.lessons.filter((lesson) => lesson.topicId === topicId);
  for (const lesson of lessons) {
    await syncGovernanceNoteForLesson(lesson.id);
  }
}

async function syncAllGovernanceNotesToOpenNotebook() {
  const bootstrap = await fetchBootstrap();
  for (const lesson of bootstrap.lessons) {
    if (lesson.notebookId) {
      await syncGovernanceNoteForLesson(lesson.id);
    }
  }
}

async function syncRolesAndAuditToOpenNotebook() {
  const bootstrap = await fetchBootstrap();
  const wrapperState = readWrapperState();
  const notebookId =
    bootstrap.defaultNotebookId ||
    bootstrap.notebooks[0]?.id ||
    bootstrap.lessons.find((lesson) => lesson.notebookId)?.notebookId;

  if (!notebookId) {
    return;
  }

  await upsertOpenNotebookNote(
    notebookId,
    rolesNoteTitle(),
    JSON.stringify(
      {
        kind: "classroom-roles",
        accounts: wrapperState.accounts
      } satisfies RolesNoteDocument,
      null,
      2
    )
  );

  await upsertOpenNotebookNote(
    notebookId,
    schoolsNoteTitle(),
    JSON.stringify(
      {
        kind: "classroom-schools",
        schools: wrapperState.schools
      } satisfies SchoolsNoteDocument,
      null,
      2
    )
  );

  await upsertOpenNotebookNote(
    notebookId,
    auditNoteTitle(),
    JSON.stringify(
      {
        kind: "classroom-audit",
        events: wrapperState.auditEvents
      } satisfies AuditNoteDocument,
      null,
      2
    )
  );
}

async function syncSourceTopics(lessonId: string) {
  const bootstrap = await fetchBootstrap();
  const wrapperState = readWrapperState();
  const lesson = bootstrap.lessons.find((item) => item.id === lessonId);
  const topic = bootstrap.topics.find((item) => item.id === lesson?.topicId);

  if (!lesson?.sourceId || !topic) {
    return;
  }

  const topicOverride = wrapperState.topicOverrides[topic.id];
  const topicLabels = dedupeStrings([
    topicOverride?.name || topic.name,
    ...((topicOverride?.prerequisiteTopicIds as string[] | undefined) || topic.prerequisiteTopicIds).map(
      (topicId) => bootstrap.topics.find((item) => item.id === topicId)?.name || topicId
    )
  ]);

  await proxyOpenNotebook(`/sources/${lesson.sourceId}`, {
    method: "PUT",
    body: JSON.stringify({
      title: lesson.title,
      topics: topicLabels
    })
  });
}
